import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { getEntityId } from '../../api/client';
import { getDifficulty } from '../../utils/cards';
import { playKnownSound } from '../../utils/sounds';

const {
  FiArrowLeft,
  FiArrowRight,
  FiChevronDown,
  FiRotateCcw,
  FiSkipForward
} = FiIcons;

function Flashcard({
  card,
  onReview,
  onSkip,
  onNext,
  onBack,
  canGoBack,
  canGoNext,
  saving
}) {
  const [flipped, setFlipped] = useState(false);
  const [originOpen, setOriginOpen] = useState(false);
  const cardId = String(getEntityId(card) || card?.front_text || 'card');

  useEffect(() => {
    setFlipped(false);
    setOriginOpen(false);
  }, [cardId]);

  const revealMeaning = () => {
    if (!saving) {
      setFlipped(true);
    }
  };

  const returnToWord = () => {
    if (!saving) {
      setFlipped(false);
      setOriginOpen(false);
    }
  };

  const handleReview = (known) => {
    if (!saving) {
      onReview(known);
    }
  };

  const handleSkip = () => {
    if (!saving) {
      onSkip();
    }
  };

  const handleBack = () => {
    if (!saving && canGoBack) {
      onBack();
    }
  };

  const handleNext = () => {
    if (!saving && canGoNext) {
      onNext();
    }
  };

  return (
    <motion.article
      className="flashcard"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      aria-live="polite"
    >
      <div className="card-meta">
        <span className={`difficulty ${getDifficulty(card.target_band).toLowerCase()}`}>
          {getDifficulty(card.target_band)}
        </span>
        <span>{card.english_variant} English</span>
      </div>

      {flipped ? (
        <MeaningFace
          card={card}
          originOpen={originOpen}
          setOriginOpen={setOriginOpen}
          onBackToWord={returnToWord}
          onReview={handleReview}
          saving={saving}
        />
      ) : (
        <WordFace
          card={card}
          onReveal={revealMeaning}
          onSkip={handleSkip}
          saving={saving}
        />
      )}

      <div className="manual-navigation" aria-label="Flashcard navigation">
        <button
          type="button"
          className="button navigation-button"
          onClick={handleBack}
          disabled={saving || !canGoBack}
        >
          <SafeIcon icon={FiArrowLeft} />
          <span>Previous</span>
        </button>

        <button
          type="button"
          className="button navigation-button next-button"
          onClick={handleNext}
          disabled={saving || !canGoNext}
        >
          <span>Next</span>
          <SafeIcon icon={FiArrowRight} />
        </button>
      </div>
    </motion.article>
  );
}

function WordFace({ card, onReveal, onSkip, saving }) {
  return (
    <div className="card-face front">
      <span className="word-type">{card.part_of_speech}</span>
      <h2>{card.front_text}</h2>

      <div className="card-actions">
        <button
          type="button"
          className="button primary"
          onClick={onReveal}
          disabled={saving}
        >
          Reveal meaning
          <SafeIcon icon={FiRotateCcw} />
        </button>

        <button
          type="button"
          className="button skip-button"
          onClick={onSkip}
          disabled={saving}
        >
          <SafeIcon icon={FiSkipForward} />
          <span>Skip</span>
        </button>
      </div>
    </div>
  );
}

function MeaningFace({
  card,
  originOpen,
  setOriginOpen,
  onBackToWord,
  onReview,
  saving
}) {
  return (
    <div className="card-face back">
      <span className="word-type">{card.part_of_speech}</span>
      <h2>{card.back_text}</h2>

      {card.example_sentence && (
        <blockquote>“{card.example_sentence}”</blockquote>
      )}

      {card.related_forms && (
        <p className="related">
          <b>Word family</b>
          {card.related_forms}
        </p>
      )}

      {card.etymology && (
        <div className="origin">
          <button
            type="button"
            onClick={() => setOriginOpen((value) => !value)}
            aria-expanded={originOpen}
          >
            Word origin
            <SafeIcon
              icon={FiChevronDown}
              className={originOpen ? 'rotate' : ''}
            />
          </button>
          {originOpen && <p>{card.etymology}</p>}
        </div>
      )}

      <div className="review-actions">
        <button
          type="button"
          className="button secondary"
          onClick={onBackToWord}
          disabled={saving}
        >
          Back to word
          <SafeIcon icon={FiRotateCcw} />
        </button>

        <button
          type="button"
          className="button learning"
          onClick={() => onReview(false)}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Still learning'}
        </button>

        <button
          type="button"
          className="button known"
          onClick={() => {
            if (!saving) {
              playKnownSound();
              onReview(true);
            }
          }}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Known'}
        </button>
      </div>
    </div>
  );
}

export default Flashcard;